/**
 * RTK Query and fetch errors arrive in mutually-incompatible shapes
 * (FetchBaseQueryError, SerializedError, axios-style ApiResponse, raw Error,
 * plain string/number). Anything calling these helpers from a `useQuery`
 * destructure already has `unknown`-ish typing, so the public API accepts
 * `unknown` and narrows internally — no more `as never` at call sites.
 *
 * Feature-flag parity note: legacy gates the equivalent
 * permission-denied handler behind `omPermissionDeniedHandling` (default
 * OFF in source, ON on live). portal2 calls `isPermissionDenied()` on
 * every fetch site by default — the handler is structurally always-on.
 * No `useFeatureFlag` wrapper is needed; if a future product decision
 * requires the gate to come back, add it here.
 */

/** Public type alias kept for documentation — call sites can pass anything. */
export type ApiErrorLike = unknown;

/** Internal narrowing — extract a status code from whatever the runtime
 * hands us, returning undefined if the shape isn't recognizable. */
function statusOf(error: unknown): number | string | undefined {
  if (error == null) return undefined;
  if (typeof error === 'number') return error;
  if (typeof error === 'string') return error;
  if (typeof error !== 'object') return undefined;
  const obj = error as Record<string, unknown>;
  // axios-style response wrapper.
  const response = obj.response as { status?: number | string } | undefined;
  if (response && (typeof response.status === 'number' || typeof response.status === 'string')) {
    return response.status;
  }
  // RTK Query FetchBaseQueryError / direct {status} shape.
  if (typeof obj.status === 'number') return obj.status;
  // fetchBaseQuery reports PARSING_ERROR when the body isn't JSON (a gateway
  // 403 with an HTML or empty body) and keeps the real code in originalStatus.
  if (typeof obj.originalStatus === 'number') return obj.originalStatus;
  if (typeof obj.status === 'string') return obj.status;
  return undefined;
}

/** True when the error represents an HTTP 403. */
export function isHttp403(error: unknown): boolean {
  const s = statusOf(error);
  return s === 403 || s === '403';
}

/** True when the error represents an HTTP 404. */
export function isHttp404(error: unknown): boolean {
  const s = statusOf(error);
  return s === 404 || s === '404';
}

/**
 * Portal-compatible "is this a permission-denied error" check. Array-aware
 * — some hooks bundle errors as `[err1, err2]` and we want to treat the
 * group as forbidden if any sub-error is 403.
 */
export function isPermissionDenied(error: unknown): boolean {
  if (Array.isArray(error)) return error.some(isPermissionDenied);
  return isHttp403(error);
}

/**
 * Best-effort extraction of the backend's human-readable message from a failed
 * request. RTK Query rejections carry the parsed response body on `data`
 * (axios-style wrappers on `response.data`); platform services emit `message`
 * or `errorMessage` in either casing. Plain `Error`s yield their own message.
 * Returns null when nothing usable exists so call sites keep their OWN
 * fallback copy — never replace a specific local fallback with generic text
 * (the dead-fallback lesson).
 */
export function apiErrorMessage(error: unknown): string | null {
  if (error == null) return null;
  if (error instanceof Error) return error.message || null;
  if (typeof error !== 'object') return null;
  const obj = error as Record<string, unknown>;
  const body =
    (obj.data as Record<string, unknown> | undefined) ??
    ((obj.response as Record<string, unknown> | undefined)?.data as
      | Record<string, unknown>
      | undefined);
  if (!body || typeof body !== 'object') return null;
  for (const key of ['message', 'Message', 'errorMessage', 'ErrorMessage']) {
    const value = body[key];
    if (typeof value === 'string' && value.trim() !== '') return value;
  }
  // Platform validation errors nest their message(s) in an `errors` array of
  // `{ errorMessage }` entries; join them so every reason surfaces.
  const errors = (body.errors ?? body.Errors) as unknown;
  if (Array.isArray(errors)) {
    const messages = errors
      .map((entry) => {
        if (!entry || typeof entry !== 'object') return null;
        const e = entry as Record<string, unknown>;
        for (const key of ['errorMessage', 'ErrorMessage', 'message', 'Message']) {
          const value = e[key];
          if (typeof value === 'string' && value.trim() !== '') return value;
        }
        return null;
      })
      .filter((m): m is string => m !== null);
    if (messages.length > 0) return messages.join(' ');
  }
  return null;
}
